import React, { useState, useMemo, useCallback, useContext } from 'react';
import styled from 'styled-components';
import * as moment from 'moment';
import { FlexCenter, Loader } from '@ubeya/shared-web/components';
import useGeneralAvailabilities from '@ubeya/shared/hooks/account/useGeneralAvailabilities';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import Header from './Header';
import AddGeneralAvailabilityModal from './AddGeneralAvailabilityModal';
import AvailabilityItem from './AvailabilityItem';

const Container = styled.div`
  width: 100%;
`;

const LoaderWrapper = styled(FlexCenter)`
  flex: 1;
`;

const statusEnum = {
  pending: 0,
  approved: 1,
  pastApproved: 2,
  notApproved: 3
};

const EmployeeGeneralAvailability = ({ employeeData }) => {
  const { employeeId, firstName } = employeeData || {};
  const { t } = useContext(ConfigContext);
  const { isLoading, mappedAvailabilitiesByEmployee, addGeneralAvailability } = useGeneralAvailabilities({
    employeeIds: [employeeId]
  });

  const [openGeneralAvailability, setOpenGeneralAvailability] = useState(false);
  const { timeFormat, unavailableByDefault } = useContext(ConfigContext);

  const mappedAvailabilitiesByDay = useMemo(() => {
    const defaultType = unavailableByDefault ? 'Not Available' : 'Available';
    const defaults = [0, 1, 2, 3, 4, 5, 6].reduce((output, day) => {
      output[day] = { day, type: defaultType, available: !unavailableByDefault };
      return output;
    }, {});

    return (mappedAvailabilitiesByEmployee[employeeId]?.board || []).reduce((output, input) => {
      output[input.day] = { ...input, available: ['Available', 'Partial'].includes(input.type) };

      return output;
    }, defaults);
  }, [employeeId, mappedAvailabilitiesByEmployee, unavailableByDefault]);

  const availabilityStatus = useMemo(() => mappedAvailabilitiesByEmployee[employeeId]?.status, [
    employeeId,
    mappedAvailabilitiesByEmployee
  ]);

  const isAvailabilityApproved = useMemo(() => availabilityStatus === statusEnum.approved, [availabilityStatus]);

  const updateStatusApproved = useCallback(
    () =>
      addGeneralAvailability({
        boards: [
          {
            employeeId,
            board: mappedAvailabilitiesByEmployee[employeeId]?.board,
            status: statusEnum.approved
          }
        ],
        employeeIds: [employeeId]
      }),
    [addGeneralAvailability, employeeId, mappedAvailabilitiesByEmployee]
  );

  const updateGeneralAvailabilities = useCallback(
    ({ board }) => {
      /* if isAvailabilityApproved === true, every change has a new pending status.
           if status === undefined (first time inserting general availability), 
           every change insert status === pending also. */

      const status =
        isAvailabilityApproved || mappedAvailabilitiesByEmployee[employeeId]?.status === undefined
          ? statusEnum.pending
          : mappedAvailabilitiesByEmployee[employeeId]?.status;

      return addGeneralAvailability({
        boards: [
          {
            employeeId,
            board,
            status
          }
        ],
        employeeIds: [employeeId]
      });
    },
    [addGeneralAvailability, employeeId, isAvailabilityApproved, mappedAvailabilitiesByEmployee]
  );

  const getFormattedStatusByType = useCallback(
    (data) => {
      const { type, start, end } = data || {};

      switch (type) {
        case 'Partial': {
          const formattedStartTime = moment(start).format(timeFormat);
          const formattedEndTime = moment(end).format(timeFormat);
          return `${t('available')} | ${formattedStartTime} - ${formattedEndTime}`;
        }
        case 'Available':
          return t('available');

        case 'Not Available':
          return t('notAvailable');

        default:
          return t('available');
      }
    },
    [t, timeFormat]
  );

  if (isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }
  return (
    <Container>
      <Header
        updateStatusApproved={updateStatusApproved}
        availabilityStatus={availabilityStatus}
        lastUpdate={mappedAvailabilitiesByEmployee[employeeId]?.lastUpdate}
        isAvailabilityApproved={isAvailabilityApproved}
        employeeName={firstName}
        board={mappedAvailabilitiesByEmployee[employeeId]}
      />

      {moment.weekdays().map((dayOfWeek, index) => (
        <AvailabilityItem
          key={index}
          data={mappedAvailabilitiesByDay?.[index]}
          index={index}
          setOpenGeneralAvailability={setOpenGeneralAvailability}
          dayOfWeek={dayOfWeek}
          getFormattedStatusByType={getFormattedStatusByType}
        />
      ))}

      {openGeneralAvailability && (
        <AddGeneralAvailabilityModal
          availabilitiesByEmployee={mappedAvailabilitiesByEmployee[employeeId]}
          updateGeneralAvailabilities={updateGeneralAvailabilities}
          selectedDay={openGeneralAvailability.selectedDay}
          data={openGeneralAvailability.data}
          onClose={() => setOpenGeneralAvailability(false)}
        />
      )}
    </Container>
  );
};

export default React.memo(EmployeeGeneralAvailability);
